import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import { ROLE_LABELS } from '@/api/types'
import { Badge, Card, DataTable, EmptyState, LoadingBlock, type Column } from '@/design'
import { ApiError } from '@/lib/api'
import { usePermissions, useSession } from '@/lib/access'

import { usersBackendFor } from './api'
import { assignableRoles, type Invite, type Member } from './types'
import './users.css'

/**
 * Settings › Users, for Owner and Administrator only (`users.manage`).
 *
 * The backend is the source of truth for who may do what: this page hides the
 * Owner role from a caller without `ownership.transfer` and never lists
 * Developer / TmT Support at all, but every refusal it can show comes from the
 * API's own message rather than from a rule restated here.
 */
export function UsersPage() {
  const client = useApiClient()
  const backend = usersBackendFor(client)
  const queryClient = useQueryClient()
  const access = usePermissions()
  const session = useSession()
  const roles = assignableRoles(access.can('ownership.transfer'))

  const members = useQuery({
    queryKey: ['tenant-members', client.kind],
    queryFn: () => backend.members(),
  })
  const invites = useQuery({
    queryKey: ['tenant-invites', client.kind],
    queryFn: () => backend.invites(),
  })

  /** Every mutation ends the same way: ask the server again, never patch locally. */
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['tenant-members'] }),
      queryClient.invalidateQueries({ queryKey: ['tenant-invites'] }),
    ])
  }

  const changeRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) => backend.changeRole(userId, role),
    onSuccess: refresh,
  })
  const removeMember = useMutation({
    mutationFn: (userId: string) => backend.removeMember(userId),
    onSuccess: refresh,
  })
  const invite = useMutation({
    mutationFn: ({ email, role }: { email: string; role: string }) => backend.invite({ email, role }),
    onSuccess: refresh,
  })
  const resendInvite = useMutation({
    mutationFn: (inviteId: string) => backend.resendInvite(inviteId),
    onSuccess: refresh,
  })
  const revokeInvite = useMutation({
    mutationFn: (inviteId: string) => backend.revokeInvite(inviteId),
    onSuccess: refresh,
  })

  const busy =
    changeRole.isPending || removeMember.isPending || resendInvite.isPending || revokeInvite.isPending

  const memberColumns: Column<Member>[] = [
    {
      key: 'person',
      header: 'Name',
      sortValue: (row) => row.display_name ?? row.email,
      cell: (row) => (
        <span className="ci-users__person">
          <strong>{row.display_name ?? row.email}</strong>
          {row.display_name ? <small>{row.email}</small> : null}
          {row.user_id === session.data?.userId ? <Badge tone="accent">You</Badge> : null}
        </span>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      sortValue: (row) => row.role.label,
      cell: (row) => (
        <label className="ci-users__role">
          <span className="ci-visually-hidden">Role for {row.display_name ?? row.email}</span>
          <select
            value={row.role.value}
            disabled={busy}
            onChange={(event) => changeRole.mutate({ userId: row.user_id, role: event.target.value })}
          >
            {/* The member's current role is always listed, even one this caller
                may not grant, so the select shows the truth rather than jumping
                to another value the moment it renders. */}
            {roleOptions(roles, row.role.value).map((value) => (
              <option key={value} value={value}>
                {ROLE_LABELS[value] ?? value}
              </option>
            ))}
          </select>
        </label>
      ),
    },
    {
      key: 'last_sign_in',
      header: 'Last sign-in',
      sortValue: (row) => row.last_sign_in_at ?? '',
      cell: (row) => <>{formatDate(row.last_sign_in_at) ?? 'Never'}</>,
    },
    {
      key: 'status',
      header: 'Status',
      // The API carries no member status; "has this person ever signed in" is
      // the distinction the screen actually needs, and it is derived here from
      // `last_sign_in_at` rather than invented on the server.
      cell: (row) =>
        row.last_sign_in_at ? (
          <Badge tone="positive">Active</Badge>
        ) : (
          <Badge tone="warning">Not signed in yet</Badge>
        ),
    },
    {
      key: 'remove',
      header: 'Remove',
      align: 'end',
      cell: (row) => (
        <button
          type="button"
          className="ci-button"
          data-variant="ghost"
          disabled={busy}
          onClick={() => {
            const who = row.display_name ?? row.email
            if (window.confirm(`Remove ${who} from this organisation? Their sessions end immediately.`)) {
              removeMember.mutate(row.user_id)
            }
          }}
        >
          Remove
        </button>
      ),
    },
  ]

  const inviteColumns: Column<Invite>[] = [
    { key: 'email', header: 'Email', sortValue: (row) => row.email, cell: (row) => <>{row.email}</> },
    { key: 'role', header: 'Role', sortValue: (row) => row.role.label, cell: (row) => <>{row.role.label}</> },
    {
      key: 'sent',
      header: 'Sent',
      sortValue: (row) => row.last_sent_at ?? row.created_at,
      cell: (row) => <>{formatDate(row.last_sent_at ?? row.created_at) ?? '—'}</>,
    },
    {
      key: 'expires',
      header: 'Expires',
      sortValue: (row) => row.expires_at,
      cell: (row) => <>{formatDate(row.expires_at) ?? '—'}</>,
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'end',
      cell: (row) => (
        <span className="ci-users__actions">
          <button
            type="button"
            className="ci-button"
            data-variant="ghost"
            disabled={busy}
            onClick={() => resendInvite.mutate(row.id)}
          >
            Resend
          </button>
          <button
            type="button"
            className="ci-button"
            data-variant="ghost"
            disabled={busy}
            onClick={() => {
              if (window.confirm(`Revoke the invitation to ${row.email}? Their link stops working.`)) {
                revokeInvite.mutate(row.id)
              }
            }}
          >
            Revoke
          </button>
        </span>
      ),
    },
  ]

  return (
    <div className="ci-users">
      <Card title="Members" subtitle="Everyone who can sign in to this organisation, and with which role.">
        <ApiMessage error={changeRole.error ?? removeMember.error} />
        {members.isPending ? <LoadingBlock rows={4} label="Loading members" /> : null}
        {members.isError ? <LoadError error={members.error} subject="members" /> : null}
        {members.data ? (
          <DataTable
            caption="Members of this organisation"
            columns={memberColumns}
            rows={members.data}
            rowKey={(row) => row.user_id}
            empty={<EmptyState variant="no-data" title="No members yet" description="Invite someone below." />}
          />
        ) : null}
      </Card>

      <Card
        title="Pending invitations"
        subtitle="Invitations that have been sent but not yet accepted."
      >
        <ApiMessage error={resendInvite.error ?? revokeInvite.error} />
        {invites.isPending ? <LoadingBlock rows={2} label="Loading invitations" /> : null}
        {invites.isError ? <LoadError error={invites.error} subject="invitations" /> : null}
        {invites.data ? (
          <DataTable
            caption="Pending invitations"
            columns={inviteColumns}
            rows={invites.data}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                variant="no-data"
                title="No pending invitations"
                description="Invitations appear here until they are accepted, revoked or expire."
              />
            }
          />
        ) : null}
      </Card>

      <InviteForm
        roles={roles}
        busy={invite.isPending}
        error={invite.error}
        onInvite={(email, role) => invite.mutate({ email, role })}
        sent={invite.isSuccess ? invite.data.email : null}
      />
    </div>
  )
}

function InviteForm({
  roles,
  busy,
  error,
  sent,
  onInvite,
}: {
  roles: readonly string[]
  busy: boolean
  error: unknown
  sent: string | null
  onInvite: (email: string, role: string) => void
}) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState(roles.includes('read_only') ? 'read_only' : (roles[0] ?? 'read_only'))

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!email.trim()) return
    onInvite(email.trim(), role)
    setEmail('')
  }

  return (
    <Card title="Invite someone" subtitle="They receive a link that signs them in and grants this role.">
      <form className="ci-users__invite" aria-label="Invite someone" onSubmit={submit}>
        <label>
          Email address
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label>
          Role
          <select name="role" value={role} onChange={(event) => setRole(event.target.value)}>
            {roles.map((value) => (
              <option key={value} value={value}>
                {ROLE_LABELS[value] ?? value}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="ci-button" data-variant="primary" disabled={busy}>
          {busy ? 'Sending…' : 'Send invitation'}
        </button>
      </form>
      <ApiMessage error={error} />
      {sent && !error ? <p role="status">Invitation sent to {sent}.</p> : null}
    </Card>
  )
}

/** Whatever the API said went wrong, in its own words. */
function ApiMessage({ error }: { error: unknown }) {
  if (!error) return null
  const message = error instanceof ApiError ? error.message : 'Something went wrong. Try again shortly.'
  return (
    <p role="alert" className="ci-users__error">
      {message}
    </p>
  )
}

function LoadError({ error, subject }: { error: unknown; subject: string }) {
  const refused = error instanceof ApiError && error.status === 403
  return (
    <EmptyState
      variant={refused ? 'permission' : 'outage'}
      description={
        refused
          ? 'You need Owner or Administrator access to manage users.'
          : `Could not load the ${subject}. Try again shortly.`
      }
    />
  )
}

/** The roles on offer, with the row's own role kept even when it is not one of them. */
function roleOptions(roles: readonly string[], current: string): string[] {
  return roles.includes(current) ? [...roles] : [current, ...roles]
}

function formatDate(value: string | null): string | null {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString('en-GB', { dateStyle: 'medium' })
}
